import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, Quote, Star, 
  User, Volume2, VolumeX, Loader2 
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import styles from './Feedback.module.css';

const Feedback = () => {
  const { t, isMuted, setIsMuted } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const videoRef = useRef(null);

  const feedbackData = [
    {
      id: 1,
      type: 'text',
      text: t('feedback_q1'),
      name: t('feedback_a1_name', 'News Channel Owner / न्यूज चैनल ओनर'),
      role: t('feedback_a1_role'),
      rating: 5
    },
    {
      id: 2,
      type: 'video',
      videoUrl: 'https://res.cloudinary.com/ddkyeuhk8/video/upload/f_auto,q_auto/v1774964873/anantainfotech/kuxujbnntsnu0kayr9mi.webm', 
      name: 'Bhumi Pandey',
      role: t('feedback_a2_role', 'Anchor, News Maharashtra'),
      quote: t('feedback_q2', "The AI anchors have revolutionized our daily broadcasting. We now produce 24/7 content with zero downtime.")
    },
    {
      id: 3,
      type: 'video',
      videoUrl: 'https://res.cloudinary.com/ddkyeuhk8/video/upload/f_auto,q_auto/v1774963976/anantainfotech/vteepobtjvwe08w9log6.mp4',
      name: 'Giridhar Maheshwari',
      role: t('feedback_a4_role', 'Director, Happy Group of schools'),
      quote: t('feedback_q4', "I was skeptical about AI, but the results are mind-blowing. Our viewers genuinely believe these are human anchors.")
    },
    {
      id: 4,
      type: 'text',
      text: t('feedback_q5', "Creating my own custom AI clone saved me 40 hours a week. It's the most profitable investment I've made for my channel this year."),
      name: 'Sneha Rao',
      role: t('feedback_a5_role', 'Journalist'),
      rating: 5
    }
  ];

  const current = feedbackData[currentIndex];

  // Reset loading state when slide changes
  useEffect(() => {
    if (current.type === 'video') {
      setIsVideoLoading(true);
    }
  }, [currentIndex, current.type]);

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % feedbackData.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + feedbackData.length) % feedbackData.length);
  };

  // Called when video is buffered enough to play smoothly
  const handleVideoReady = () => {
    setIsVideoLoading(false);
    if (videoRef.current) {
      videoRef.current.play().catch(err => console.log("Autoplay blocked:", err));
    }
  };

  const variants = {
    enter: (direction) => ({ x: direction > 0 ? 500 : -500, opacity: 0 }),
    center: { zIndex: 1, x: 0, opacity: 1 },
    exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 500 : -500, opacity: 0 })
  };

  return (
    <section className={styles.feedbackSection} id="feedback">
      <div className={styles.glowOrb} />
      
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.badge}>{t('feedback_badge')}</div>
          <h2 className={styles.title}>
            {t('feedback_title')} <span className={styles.gradientText}>{t('feedback_title_accent')}</span>
          </h2>
        </div>

        <div className={styles.carouselContainer}>
          <button className={styles.navBtn} onClick={handlePrev}><ChevronLeft size={24} /></button>

          <div className={styles.cardViewport}>
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className={styles.feedbackCard}
              >
                {/* VISUAL SIDE */}
                <div className={styles.visualSide}>
                  {current.type === 'video' ? (
                    <div className={styles.portraitFrame}>
                      {/* Loading Shimmer Overlay */}
                      {isVideoLoading && (
                        <div className={styles.shimmerOverlay}>
                          <Loader2 className={styles.spinner} size={40} />
                          <span>Loading High Quality Video...</span>
                        </div>
                      )}
                      
                      <video 
                        ref={videoRef}
                        src={current.videoUrl} 
                        muted={isMuted} 
                        loop 
                        playsInline
                        className={`${styles.videoPlayer} ${isVideoLoading ? styles.hidden : styles.visible}`}
                        onCanPlayThrough={handleVideoReady}
                        preload="auto"
                      />

                      <div className={styles.videoOverlay}>
                        <div className={styles.tag}>9:16 BROADCAST</div>
                        <button 
                          className={styles.volumeToggle} 
                          onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
                        >
                          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} color="#e87355" />}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.placeholderSide}>
                      <Quote className={styles.quoteIconGhost} size={80} />
                      <div className={styles.brandBadge}>ANANTA AI</div>
                    </div>
                  )}
                </div>

                {/* CONTENT SIDE */}
                <div className={styles.contentSide}>
                  <div className={styles.stars}>
                    {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="#e87355" color="#e87355" />)}
                  </div>
                  
                  <div className={styles.textContainer}>
                    <p className={styles.mainQuote}>
                      {current.type === 'video' ? `"${current.quote}"` : `"${current.text}"`}
                    </p>
                  </div>

                  <div className={styles.authorMeta}>
                    <div className={styles.avatar}>
                      {current.type === 'text' ? current.name.charAt(0) : <User size={22} />}
                    </div>
                    <div className={styles.authorInfo}>
                      <h4 className={styles.name}>{current.name}</h4>
                      <p className={styles.role}>{current.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <button className={styles.navBtn} onClick={handleNext}><ChevronRight size={24} /></button>
        </div>

        <div className={styles.indicatorWrapper}>
          {feedbackData.map((_, idx) => (
            <div 
              key={idx} 
              className={`${styles.dot} ${idx === currentIndex ? styles.activeDot : ''}`}
              onClick={() => { setDirection(idx > currentIndex ? 1 : -1); setCurrentIndex(idx); }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Feedback;